import { Container } from "react-bootstrap";
import { useQuery, useQueryClient } from "react-query";
import { Link, useParams } from "react-router-dom";
import { useEffect, useState, useMemo } from "react";

import UserDetails from "../components/UserDetails";
import UserTabDetail from "../components/UserDetails/UserTabDetail";
import ErrorPage from "../components/ErrorPage";
import { getUser } from "../services/userServices";
import { canEditCustomerAddress } from "../helpers/customerAddressAccess";
import { canEditCustomerLegalName } from "../helpers/customerLegalNameAccess";
import { useAuth } from "../contexts/AuthContext";
import { QueryKeyEnum } from "../types";
import { CardDetail, CardStatus } from "../types/CardDetail";
import { UserType } from "../types/user";

import styles from "../styles/user.module.scss";
import PageLoader from "../components/PageLoader";
import StatusBadge from "../components/StatusBadge";
import MambuDetail from "../components/UserDetails/MambuDetail";
import DisableAccountModal from "../components/UserDetails/DisableAccountModal";
import EnableAccountModal from "../components/UserDetails/EnableAccountModal";
import MobileNumberModal from "../components/UserDetails/MobileNumberModal";
import UpdateAddressModal from "../components/UserDetails/UpdateAddressModal";
import UpdateLegalNameModal from "../components/UserDetails/UpdateLegalNameModal";

import { CopyButton } from "../components/UserDetails/CopyButton";
import { featureFlags, EnvironmentNames } from "../featureFlags";
import { getSignedInAdminId, rememberRecentUser } from "../helpers/recentUsers";
import { isAeUser } from "../helpers/utils";

export default function User() {
  const { userId } = useParams();
  const { assignedRoles, assignedCountries } = useAuth();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEnableModalVisible, setIsEnableModalVisible] = useState(false);
  const [isDisabled, setDisabled] = useState(false);
  const [isCognitoDisabled, setCognitoDisabled] = useState(false);
  const [mobileModalAction, setMobileModalAction] = useState<
    "update" | "clear" | null
  >(null);
  const [isMobileModalVisible, setIsMobileModalVisible] = useState(false);
  const [isAddressModalVisible, setIsAddressModalVisible] = useState(false);
  const [isLegalNameModalVisible, setIsLegalNameModalVisible] = useState(false);

  const { error, data, isLoading, refetch } = useQuery<UserType, Error>(
    ["get-user-details", userId],
    () => getUser(userId || "")
  );
  const queryClient = useQueryClient();

  useEffect(() => {
    const isTerminated = !!data?.is_disabled;
    const isCognitoDisabled = !!data?.cognito_status?.enabled === false;
    setDisabled(isTerminated || isCognitoDisabled);
    setCognitoDisabled(isCognitoDisabled);
  }, [data]);

  useEffect(() => {
    const userId = data?.id;
    if (!userId) return;
    let active = true;
    getSignedInAdminId().then((adminId) => {
      if (!active || !adminId) return;
      rememberRecentUser(adminId, userId);
    });
    return () => {
      active = false;
    };
  }, [data?.id]);

  const enableTerminateCard = useMemo(() => {
    const stage = process.env.REACT_APP_STAGE as EnvironmentNames;
    return featureFlags[stage]?.enableTerminateCard;
  }, []);

  const onCloseModal = () => setIsModalVisible(false);
  const onCloseEnableModal = () => setIsEnableModalVisible(false);

  const onUpdateMobile = () => {
    setMobileModalAction("update");
    setIsMobileModalVisible(true);
  };

  const onClearMobile = () => {
    setMobileModalAction("clear");
    setIsMobileModalVisible(true);
  };

  const onCloseMobileModal = () => {
    setIsMobileModalVisible(false);
    setMobileModalAction(null);
  };

  const onMobileActionSuccess = () => {
    refetch();
  };

  const onUpdateAddress = () => setIsAddressModalVisible(true);
  const onCloseAddressModal = () => setIsAddressModalVisible(false);
  const onAddressUpdateSuccess = () => {
    refetch();
  };

  const onUpdateLegalName = () => setIsLegalNameModalVisible(true);
  const onCloseLegalNameModal = () => setIsLegalNameModalVisible(false);
  const onLegalNameUpdateSuccess = () => {
    refetch();
  };

  const onApproved = () => {
    setDisabled(true);

    if (enableTerminateCard) {
      const cardDetail = queryClient.getQueryData<CardDetail>([
        QueryKeyEnum.GetCardDetail,
        userId,
      ]);

      if (cardDetail && cardDetail.status !== CardStatus.PermBlock) {
        queryClient.setQueryData<CardDetail>(
          [QueryKeyEnum.GetCardDetail, userId],
          {
            ...cardDetail,
            status: CardStatus.PermBlock,
          }
        );
      }
    }

    refetch();
    queryClient.invalidateQueries(["get-deposit-bank-detail", userId]);
  };

  const onEnableApproved = () => {
    setDisabled(false);
    setCognitoDisabled(false);

    refetch();
    queryClient.invalidateQueries(["get-deposit-bank-detail", userId]);
  };

  if (isLoading)
    return <PageLoader isLoading={isLoading} content="Loading users.." />;

  if (error || !data) return <ErrorPage />;

  const showLedgerActivity = isAeUser(data);
  const customerEditAccess = {
    appCountry: data.app_country,
    assignedRoles,
    assignedCountries,
    authorizationEnabled:
      !!featureFlags[process.env.REACT_APP_STAGE as EnvironmentNames]
        ?.enableAdminAuthorization,
  };
  const showUpdateAddress = canEditCustomerAddress(customerEditAccess);
  const showUpdateLegalName = canEditCustomerLegalName(customerEditAccess);

  return (
    <Container className={styles.wrapper}>
      <Link className={styles.backToUserLink} to="/users">
        <span className="material-icons material-icons-outlined">
          chevron_left
        </span>
        Back to all users
      </Link>
      <div className={styles.boxTitleUserDetail}>
        <div className={styles.titleUserDetailName}>
          <h2 className={styles.titleUserDetail}>{data.full_name}</h2>
          {showUpdateLegalName && (
            <button
              className={styles.actionButton}
              onClick={onUpdateLegalName}
              title="Update legal name"
              data-testid="update-legal-name-button"
            >
              <span className="material-icons material-icons-outlined">
                edit
              </span>
            </button>
          )}
        </div>
        <div className={styles.titleUserDetailRightBox}>
          {isDisabled || isCognitoDisabled ? (
            <>
              <span className={styles.disbledUser}>
                DISABLED
                {!isDisabled && isCognitoDisabled && <span>(LOGIN)</span>}
              </span>
              <div
                className={styles.titleRight}
                onClick={() => setIsEnableModalVisible(true)}
              >
                Re-enable account
              </div>
            </>
          ) : (
            <div
              className={styles.titleRight}
              onClick={() => setIsModalVisible(true)}
            >
              Disable account
            </div>
          )}
          <MambuDetail userId={userId} hidden={showLedgerActivity} />
          {showLedgerActivity && userId ? (
            <Link
              className={styles.titleRight}
              to={`/ledger-activity?userId=${encodeURIComponent(userId)}`}
              data-testid="user-ledger-activity-link"
            >
              <span>User’s ledger activity</span>
              <span className="material-icons material-icons-outlined">
                chevron_right
              </span>
            </Link>
          ) : null}
          {userId ? (
            <Link
              className={styles.titleRight}
              to={`/camp-transactions?userId=${encodeURIComponent(userId)}`}
              data-testid="user-camp-transactions-link"
            >
              <span>User’s CAMP activity</span>
              <span className="material-icons material-icons-outlined">
                chevron_right
              </span>
            </Link>
          ) : null}
        </div>
      </div>
      <div className={styles.copyToClipboard} style={{ marginBottom: "20px" }}>
        <span>{data.id}</span>
        <CopyButton text={data.id!} />
      </div>

      <StatusBadge
        status={data.kyc_passed ? "KYC PASSED" : "KYC NOT PASSED"}
        emphasis={data.kyc_passed ? "success" : "warning"}
      />
      <div className={styles.boxUserDetail}>
        <UserDetails
          data={data}
          isCognitoDisabled={isCognitoDisabled}
          onUpdateMobile={onUpdateMobile}
          onClearMobile={onClearMobile}
          onUpdateAddress={showUpdateAddress ? onUpdateAddress : undefined}
        />
        <UserTabDetail userData={data} />
      </div>
      <DisableAccountModal
        name={data.full_name}
        phoneNumber={`+${data.mobile_country_code}${data.mobile}`}
        email={data.email}
        onClose={onCloseModal}
        onApproved={onApproved}
        isVisible={isModalVisible}
      />
      <EnableAccountModal
        name={data.full_name}
        phoneNumber={`+${data.mobile_country_code}${data.mobile}`}
        email={data.email}
        onClose={onCloseEnableModal}
        onApproved={onEnableApproved}
        isVisible={isEnableModalVisible}
      />
      <MobileNumberModal
        action={mobileModalAction}
        currentMobile={`+${data.mobile_country_code}${data.mobile}`}
        isVisible={isMobileModalVisible}
        onClose={onCloseMobileModal}
        onSuccess={onMobileActionSuccess}
      />
      {showUpdateAddress && (
        <UpdateAddressModal
          user={data}
          isVisible={isAddressModalVisible}
          onClose={onCloseAddressModal}
          onSuccess={onAddressUpdateSuccess}
        />
      )}
      {showUpdateLegalName && (
        <UpdateLegalNameModal
          fullName={data.full_name}
          isVisible={isLegalNameModalVisible}
          onClose={onCloseLegalNameModal}
          onSuccess={onLegalNameUpdateSuccess}
        />
      )}
    </Container>
  );
}
